// PATH: src/components/trials/FactSideView.tsx
import * as React from "react";
import { Box, Stack, Typography } from "@mui/material";
import type { TrialSide } from "../../models/domain";
import { StatusPill } from "../ui";
import { FACT_KIND_LABELS, disabledByLabel, disabledMap, factLines, reviewObjections, sideCounts } from "./trialLogic";

interface Props {
  title: string;
  side: TrialSide | null;
  /** Ce qu'on dit quand ce côté n'a rien. */
  emptyLabel: string;
}

/**
 * Un côté du face-à-face : tous les faits de l'unité tels qu'extraits, les
 * faits écartés barrés avec leur note, et sous chaque fait le verdict du
 * relecteur (passage, type, langue, note).
 */
export const FactSideView: React.FC<Props> = ({ title, side, emptyLabel }) => {
  const lines = factLines(side?.unit);
  const off = disabledMap(side);
  const counts = sideCounts(side);
  const reviewed = side ? Object.keys(side.review ?? {}).length > 0 : false;

  return (
    <Box sx={{ minWidth: 0 }}>
      <Stack direction="row" spacing={1} alignItems="baseline" flexWrap="wrap" useFlexGap sx={{ mb: 0.75 }}>
        <Typography variant="subtitle2">{title}</Typography>
        {side && (
          <Typography variant="caption" color="text.secondary">
            {counts.total} fait{counts.total > 1 ? "s" : ""}
            {counts.disabled ? `, dont ${counts.disabled} écarté${counts.disabled > 1 ? "s" : ""}` : ""}
            {" · "}
            <span className="mono">{side.model_name}</span>
            {" · "}
            {reviewed ? `relu à ${Math.round((side.review_coverage ?? 0) * 100)} %` : "non relu"}
          </Typography>
        )}
      </Stack>

      {!side || lines.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ fontStyle: "italic" }}>
          {emptyLabel}
        </Typography>
      ) : (
        <Stack spacing={0.75}>
          {lines.map((l) => {
            const disabled = off.get(l.id);
            const review = side.review?.[l.id];
            const objections = reviewObjections(review);
            return (
              <Box
                key={l.id}
                sx={{ pl: 1, borderLeft: 2, borderColor: disabled ? "divider" : objections.length ? "warning.main" : "primary.light" }}
              >
                <Stack direction="row" spacing={0.75} alignItems="baseline" flexWrap="wrap" useFlexGap>
                  <Typography variant="caption" color="text.secondary">
                    {FACT_KIND_LABELS[l.kind]}
                  </Typography>
                  <Typography
                    variant="body2"
                    component="span"
                    dir="auto"
                    sx={{
                      fontWeight: l.kind === "term" || l.kind === "category" ? 600 : undefined,
                      textDecoration: disabled ? "line-through" : undefined,
                      color: disabled ? "text.disabled" : undefined,
                    }}
                  >
                    {l.text}
                  </Typography>
                  <Typography variant="caption" className="mono" color="text.secondary">
                    {l.id}
                  </Typography>
                </Stack>
                {l.detail && (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    dir="auto"
                    sx={{ textDecoration: disabled ? "line-through" : undefined }}
                  >
                    {l.detail}
                  </Typography>
                )}
                {(disabled || review) && (
                  <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mt: 0.25 }}>
                    {disabled && <StatusPill tone="neutral">{disabledByLabel(disabled.disabled_by)}</StatusPill>}
                    {review && objections.length === 0 && <StatusPill tone="success">confirmé par le relecteur</StatusPill>}
                    {objections.map((o) => (
                      <StatusPill key={o.label} tone={o.tone}>
                        {o.label}
                      </StatusPill>
                    ))}
                  </Stack>
                )}
                {disabled?.note && (
                  <Typography variant="caption" color="text.secondary" dir="auto" component="div">
                    Note : {disabled.note}
                  </Typography>
                )}
                {review?.note && review.note !== disabled?.note && (
                  <Typography variant="caption" color="text.secondary" dir="auto" component="div">
                    Relecteur : {review.note}
                  </Typography>
                )}
              </Box>
            );
          })}
        </Stack>
      )}
    </Box>
  );
};

export default FactSideView;
